import { useConsumptionData } from "@/api/consumption";
import { formatCurrency } from "@/lib/format-for-view";
import { format, isSameMonth, parseISO, startOfYear, subDays } from "date-fns";
import { fr } from "date-fns/locale";
import { useMemo } from "react";
import {
	Bar,
	BarChart,
	Cell,
	LabelList,
	Legend,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

interface DataItem {
	name: string;
	Euro: number;
	EuroBaseline: number;
	trend: number;
	date: string;
}

const BgtComparisonChart = () => {
	const currentDate = new Date();
	const startOfCurrentYear = startOfYear(currentDate);

	// Fetch consumption data for the current year
	const {
		data: apiData,
		isLoading,
		error,
	} = useConsumptionData({
		energy: "ELEC",
		granularity: "MONTHLY",
		start_date: format(startOfCurrentYear, "yyyy-MM-dd"),
		end_date: format(subDays(currentDate, 2), "yyyy-MM-dd"),
		include_baseline: true,
	});

	const chartData = useMemo(() => {
		if (!apiData?.data) {
			return [];
		}
		if (apiData?.data.length <= 2) {
			return [];
		}

		// Transform the data to find highest, current, and lowest months
		const monthlyData = apiData.data.map((item) => ({
			date: parseISO(item.timestamp),
			cost: Number(item.cost),
			cost_base: Number(item.cost_base),
			average_100_cost: Number(item.average_100_cost),
		}));

		// Get current month data
		const currentMonth = monthlyData[monthlyData.length - 1];

		// Exclude current month when finding highest and lowest
		const historicalData = monthlyData.slice(0, -1);

		// Find highest and lowest months from historical data
		const highestMonth = historicalData.reduce(
			(max, current) => (current.cost > max.cost ? current : max),
			historicalData[0],
		);

		const lowestMonth = historicalData.reduce(
			(min, current) => (current.cost < min.cost ? current : min),
			historicalData[0],
		);

		const today = new Date();

		const validMonths = monthlyData.filter((month) => {
			const monthDate = new Date(month.date);
			return !isSameMonth(monthDate, today);
		});

		const baseline =
			validMonths.reduce((sum, month) => sum + month.cost, 0) /
			validMonths.length;

		const formattedData = [
			{
				name: "Mois le plus cher",
				Euro: highestMonth?.cost,
				EuroBaseline: baseline,
				trend: ((highestMonth?.cost - baseline) / baseline) * 100,
				date: format(highestMonth?.date, "MMM yyyy", { locale: fr }),
			},
			{
				name: "Mois actuel",
				Euro: currentMonth?.cost,
				EuroBaseline: baseline,
				trend: ((currentMonth?.cost - baseline) / baseline) * 100,
				date: format(currentMonth?.date, "MMM yyyy", { locale: fr }),
			},
			{
				name: "Mois le moins cher",
				Euro: lowestMonth?.cost,
				EuroBaseline: baseline,
				trend: ((lowestMonth?.cost - baseline) / baseline) * 100,
				date: format(lowestMonth?.date, "MMM yyyy", { locale: fr }),
			},
		];

		return formattedData;
	}, [apiData]);

	const CustomTooltip = ({
		active,
		payload,
	}: {
		active?: boolean;
		payload?: { payload: DataItem }[];
	}) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;

			// Make sure we're working with a number
			const trendValue = Number(data.trend);

			// Format the trend for display
			const displayTrend =
				Number.isFinite(trendValue) && !Number.isNaN(trendValue)
					? trendValue > 0
						? `+${trendValue.toFixed(1)}%`
						: `${trendValue.toFixed(1)}%`
					: "N/A";

			const trendColor =
				trendValue > 0
					? "text-red-500"
					: trendValue < 0
						? "text-green-500"
						: "text-gray-500";

			return (
				<div className="bg-white border p-2 rounded shadow text-xs max-w-[180px]">
					<p className="font-medium">
						<span className={trendColor}>{displayTrend}</span> par rapport à la
						base
					</p>
					<p className="text-gray-700 mt-1">{data.date}</p>
				</div>
			);
		}
		return null;
	};

	const CustomLegend = () => {
		return (
			<div className="flex justify-center items-center text-sm text-gray-600 mt-6">
				<div className="w-4 h-1 bg-red-500 mr-2" />
				Moyenne de l'année en cours
			</div>
		);
	};

	if (apiData?.data?.length === 0) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Aucune donnée trouvée pour cette période
					</div>
				</CardContent>
			</Card>
		);
	}

	if (!isLoading && (apiData?.data?.length || 0) <= 2) {
		return null;
	}

	if (error) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Erreur lors du chargement des données de consommation. Veuillez
						réessayer ultérieurement.
					</div>
				</CardContent>
			</Card>
		);
	}

	if (isLoading) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="animate-pulse space-y-4">
						<div className="h-4 bg-gray-200 rounded w-1/4" />
						<div className="h-6 bg-gray-200 rounded w-3/4" />
						<div className="h-8 bg-gray-200 rounded w-1/2" />
						<div className="h-4 bg-gray-200 rounded w-2/3" />
					</div>
				</CardContent>
			</Card>
		);
	}

	if (!chartData.length) {
		return (
			<Card className="max-w-full md:max-w-md mx-auto p-4 shadow-lg rounded-lg bg-white border border-gray-100">
				<CardContent className="h-64 sm:h-72 flex items-center justify-center">
					<p className="text-gray-500">Aucune donnée disponible</p>
				</CardContent>
			</Card>
		);
	}

	const baselineValue = chartData[0].EuroBaseline;

	return (
		<Card className="w-full max-w-full overflow-hidden border-gray-200 dark:border-gray-800 shadow-sm">
			<CardHeader className="pb-1 pt-3 px-3 sm:px-4 space-y-0">
				<div className="flex justify-between items-center">
					<CardTitle className="text-lg font-bold">Comparaison</CardTitle>
				</div>
				<p className="text-xs text-gray-500">
					Comparez vos données sur les 12 derniers mois
				</p>
			</CardHeader>

			<CardContent className="px-0">
				<div className="w-full h-64 sm:h-72">
					<ResponsiveContainer width="100%" height="100%">
						<BarChart
							data={chartData}
							barCategoryGap="30%"
							margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
						>
							<XAxis
								dataKey="name"
								tickLine={false}
								axisLine={false}
								interval={0}
								tick={({ x, y, payload }) => (
									<g>
										<text
											x={x}
											y={y + 10}
											textAnchor="middle"
											fontSize={11}
											fill="#555"
										>
											{chartData
												.find((item) => item.name === payload.value)
												?.date.replace(/\n/g, " ")}
										</text>
										<text
											x={x}
											y={y + 25}
											textAnchor="middle"
											fontSize={10}
											fill="#666"
										>
											{payload.value}
										</text>
									</g>
								)}
							/>
							<Tooltip
								content={<CustomTooltip />}
								cursor={{ fill: "rgba(0,0,0,0.05)" }}
							/>

							{/* ReferenceLine for baseline */}
							<ReferenceLine
								y={baselineValue}
								stroke="#FF0000"
								strokeWidth={2}
								strokeDasharray="3 3"
							/>

							{/* Bar without active state effect */}
							<Bar
								dataKey="Euro"
								radius={[8, 8, 0, 0]}
								isAnimationActive={true}
							>
								{chartData.map((entry, index) => (
									<Cell
										key={`cell-${entry.name}-${index}`}
										fill={
											index === 0
												? "#F97316"
												: index === 1
													? "#0EA5E9"
													: "#EC4899"
										}
										fillOpacity={0.85}
									/>
								))}
								<LabelList
									dataKey="Euro"
									position="top"
									content={(props) => {
										const { x = 0, y = 0, width = 0, value = 0 } = props;
										return (
											<text
												x={Number(x) + Number(width) / 2}
												y={Number(y) - 10}
												fill="#444"
												textAnchor="middle"
												fontSize={12}
												fontWeight="500"
											>
												{formatCurrency(Number(value))}
											</text>
										);
									}}
								/>
							</Bar>

							{/* Custom Legend */}
							<Legend
								content={<CustomLegend />}
								verticalAlign="bottom"
								height={30}
							/>
						</BarChart>
					</ResponsiveContainer>
				</div>
			</CardContent>
		</Card>
	);
};

export default BgtComparisonChart;
